// Editor constants, templates, geometry utils, and export functions

// ==================== CONSTANTS ====================
export const GRID_SIZE = 20;
export const SNAP_THRESHOLD = 6;
export const RULER_SIZE = 24;
export const MIN_ZOOM = 0.1;
export const MAX_ZOOM = 8;
export const HANDLE_SIZE = 7;

export const TOOLS = {
  SELECT: 'select',
  DIRECT_SELECT: 'direct-select',
  ANCHOR_POINT: 'anchor-point',
  LASSO: 'lasso',
  MAGIC_WAND: 'magic-wand',
  RECT: 'rect',
  ELLIPSE: 'ellipse',
  LINE: 'line',
  POLYGON: 'polygon',
  PEN: 'pen',
  CURVATURE: 'curvature',
  TEXT: 'text',
  ARROW: 'arrow',
  CONNECTOR_STRAIGHT: 'connector-straight',
  CONNECTOR_CURVED: 'connector-curved',
  CONNECTOR_ELBOW: 'connector-elbow',
  PAN: 'pan',
  ARTBOARD: 'artboard',
  BLEND: 'blend',
};

export const ARROW_STYLES = [
  { id: 'triangle', name: 'Triangle', path: 'M 0 0 L 12 5 L 0 10 Z' },
  { id: 'open', name: 'Open', path: 'M 0 0 L 10 5 L 0 10', fill: 'none' },
  { id: 'diamond', name: 'Diamond', path: 'M 0 5 L 6 0 L 12 5 L 6 10 Z' },
  { id: 'circle', name: 'Circle', path: 'M 10 5 A 5 5 0 1 0 0 5 A 5 5 0 1 0 10 5 Z' },
  { id: 'bar', name: 'Bar', path: 'M 0 0 L 0 10' },
];

export const DASH_STYLES = [
  { id: 'solid', name: 'Solid', value: '' },
  { id: 'dashed', name: 'Dashed', value: '8 4' },
  { id: 'dotted', name: 'Dotted', value: '2 4' },
  { id: 'dash-dot', name: 'Dash-Dot', value: '8 4 2 4' },
  { id: 'long-dash', name: 'Long Dash', value: '16 6' },
];

export const LINE_CAPS = ['butt', 'round', 'square'];
export const LINE_JOINS = ['miter', 'round', 'bevel'];

export const VARIABLE_WIDTH_PROFILES = [
  { id: 'uniform', name: 'Uniform', widths: [1, 1, 1, 1, 1] },
  { id: 'taper-end', name: 'Taper End', widths: [1, 1, 0.8, 0.5, 0.1] },
  { id: 'taper-start', name: 'Taper Start', widths: [0.1, 0.5, 0.8, 1, 1] },
  { id: 'taper-both', name: 'Taper Both', widths: [0.1, 0.6, 1, 0.6, 0.1] },
  { id: 'bulge', name: 'Bulge', widths: [0.5, 0.8, 1.2, 0.8, 0.5] },
  { id: 'calligraphic', name: 'Calligraphic', widths: [0.2, 1, 0.8, 1, 0.2] },
];

export const DEFAULT_STYLE = {
  fill: '#0D9488',
  stroke: '#0B1D3A',
  strokeWidth: 2,
  opacity: 1,
  dashStyle: '',
  lineCap: 'round',
  fontSize: 16,
  fontFamily: 'IBM Plex Sans',
  fontWeight: 'normal',
  fontStyle: 'normal',
  textAlign: 'left',
  lineHeight: 1.4,
  letterSpacing: 0,
  arrowStart: 'none',
  arrowEnd: 'triangle',
};

export const FONT_OPTIONS = [
  'IBM Plex Sans', 'IBM Plex Mono', 'Arial', 'Helvetica', 'Georgia',
  'Times New Roman', 'Courier New', 'Verdana', 'Palatino',
];

export const COLOR_PALETTE = [
  '#0B1D3A', '#1E293B', '#475569', '#94A3B8', '#E2E8F0', '#FFFFFF',
  '#0D9488', '#14B8A6', '#2DD4BF', '#99F6E4',
  '#3B82F6', '#60A5FA', '#93C5FD', '#DBEAFE',
  '#D97706', '#F59E0B', '#FCD34D', '#FEF3C7',
  '#7C3AED', '#8B5CF6', '#A78BFA', '#EDE9FE',
  '#DC2626', '#EF4444', '#FCA5A5', '#FEE2E2',
  '#059669', '#10B981', '#6EE7B7', '#D1FAE5',
  '#DB2777', '#EC4899', '#F9A8D4', '#FCE7F3',
];

export const KEYBOARD_SHORTCUTS = {
  'v': TOOLS.SELECT,
  'd': TOOLS.DIRECT_SELECT,
  'r': TOOLS.RECT,
  'e': TOOLS.ELLIPSE,
  'l': TOOLS.LINE,
  'p': TOOLS.PEN,
  'c': TOOLS.CURVATURE,
  't': TOOLS.TEXT,
  'a': TOOLS.ARROW,
  'f': TOOLS.ARTBOARD,
  'h': TOOLS.PAN,
  'q': TOOLS.LASSO,
  'w': TOOLS.MAGIC_WAND,
  'b': TOOLS.BLEND,
};

// ==================== ID GENERATOR ====================
let _nextId = Date.now();
export const genId = () => `obj-${_nextId++}`;

// ==================== GEOMETRY ====================
export const getBBox = (obj) => {
  if (obj.type === 'line' || obj.type === 'arrow' || obj.type === 'connector-straight' ||
      obj.type === 'connector-curved' || obj.type === 'connector-elbow') {
    const x = Math.min(obj.x1, obj.x2);
    const y = Math.min(obj.y1, obj.y2);
    return { x, y, w: Math.abs(obj.x2 - obj.x1) || 1, h: Math.abs(obj.y2 - obj.y1) || 1 };
  }
  if (obj.type === 'path' && obj.d) {
    // Parse all numbers from the d string to compute real bounding box
    const nums = obj.d.match(/-?\d+\.?\d*/g);
    if (nums && nums.length >= 2) {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (let i = 0; i < nums.length - 1; i += 2) {
        const px = parseFloat(nums[i]);
        const py = parseFloat(nums[i + 1]);
        if (isFinite(px) && isFinite(py)) {
          minX = Math.min(minX, px);
          minY = Math.min(minY, py);
          maxX = Math.max(maxX, px);
          maxY = Math.max(maxY, py);
        }
      }
      if (isFinite(minX)) {
        return { x: minX, y: minY, w: Math.max(maxX - minX, 1), h: Math.max(maxY - minY, 1) };
      }
    }
    return { x: obj.x || 0, y: obj.y || 0, w: obj.w || 100, h: obj.h || 100 };
  }
  if (obj.type === 'group' && obj.children) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const child of obj.children) {
      const cb = getBBox(child);
      minX = Math.min(minX, cb.x);
      minY = Math.min(minY, cb.y);
      maxX = Math.max(maxX, cb.x + cb.w);
      maxY = Math.max(maxY, cb.y + cb.h);
    }
    if (isFinite(minX)) return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
    return { x: 0, y: 0, w: 0, h: 0 };
  }
  return { x: obj.x || 0, y: obj.y || 0, w: obj.w || 0, h: obj.h || 0 };
};

// ==================== PATH TRANSFORMS ====================
const parsePathSegments = (d) => {
  const segments = [];
  const regex = /([MmLlHhVvCcSsQqTtAaZz])([^MmLlHhVvCcSsQqTtAaZz]*)/g;
  let match;
  while ((match = regex.exec(d)) !== null) {
    const cmd = match[1];
    const argsStr = match[2].trim();
    const args = argsStr ? argsStr.match(/-?\d+\.?\d*/g)?.map(Number) || [] : [];
    segments.push({ cmd, args: [...args] });
  }
  return segments;
};

const segmentsToPath = (segments) =>
  segments.map(s => s.cmd === 'Z' || s.cmd === 'z' ? s.cmd : `${s.cmd} ${s.args.map(n => Math.round(n * 100) / 100).join(' ')}`).join(' ');

export const translatePathD = (d, dx, dy) => {
  if (!d || (dx === 0 && dy === 0)) return d;
  const segments = parsePathSegments(d);
  for (const seg of segments) {
    const { cmd, args } = seg;
    if (cmd === 'Z' || cmd === 'z') continue;
    if (cmd === cmd.toLowerCase()) continue; // relative — no change
    switch (cmd) {
      case 'H': for (let i = 0; i < args.length; i++) args[i] += dx; break;
      case 'V': for (let i = 0; i < args.length; i++) args[i] += dy; break;
      case 'A':
        for (let i = 0; i < args.length; i += 7) { args[i + 5] += dx; args[i + 6] += dy; }
        break;
      default: // M, L, T, C, S, Q — all pairs of x,y
        for (let i = 0; i < args.length; i += 2) { args[i] += dx; if (i + 1 < args.length) args[i + 1] += dy; }
    }
  }
  return segmentsToPath(segments);
};

export const scalePathD = (d, origBBox, newBBox) => {
  if (!d || !origBBox || !newBBox) return d;
  const sx = origBBox.w > 0 ? newBBox.w / origBBox.w : 1;
  const sy = origBBox.h > 0 ? newBBox.h / origBBox.h : 1;
  const ox = origBBox.x, oy = origBBox.y;
  const nx = newBBox.x, ny = newBBox.y;
  const segments = parsePathSegments(d);
  for (const seg of segments) {
    const { cmd, args } = seg;
    if (cmd === 'Z' || cmd === 'z') continue;
    if (cmd === cmd.toLowerCase()) {
      // relative commands: scale deltas
      switch (cmd) {
        case 'h': for (let i = 0; i < args.length; i++) args[i] *= sx; break;
        case 'v': for (let i = 0; i < args.length; i++) args[i] *= sy; break;
        case 'a':
          for (let i = 0; i < args.length; i += 7) { args[i] *= sx; args[i + 1] *= sy; args[i + 5] *= sx; args[i + 6] *= sy; }
          break;
        default:
          for (let i = 0; i < args.length; i += 2) { args[i] *= sx; if (i + 1 < args.length) args[i + 1] *= sy; }
      }
      continue;
    }
    // absolute commands: transform relative to bounding box origin
    switch (cmd) {
      case 'H': for (let i = 0; i < args.length; i++) args[i] = nx + (args[i] - ox) * sx; break;
      case 'V': for (let i = 0; i < args.length; i++) args[i] = ny + (args[i] - oy) * sy; break;
      case 'A':
        for (let i = 0; i < args.length; i += 7) { args[i] *= sx; args[i + 1] *= sy; args[i + 5] = nx + (args[i + 5] - ox) * sx; args[i + 6] = ny + (args[i + 6] - oy) * sy; }
        break;
      default: // M, L, T, C, S, Q
        for (let i = 0; i < args.length; i += 2) { args[i] = nx + (args[i] - ox) * sx; if (i + 1 < args.length) args[i + 1] = ny + (args[i + 1] - oy) * sy; }
    }
  }
  return segmentsToPath(segments);
};

export const snapToGrid = (val, gridSize = GRID_SIZE) =>
  Math.round(val / gridSize) * gridSize;

export const getSnapGuides = (draggedBBox, allObjects, selectedIds, threshold = SNAP_THRESHOLD) => {
  const guides = [];
  const cx = draggedBBox.x + draggedBBox.w / 2;
  const cy = draggedBBox.y + draggedBBox.h / 2;

  for (const obj of allObjects) {
    if (selectedIds.includes(obj.id)) continue;
    const bbox = getBBox(obj);
    const ocx = bbox.x + bbox.w / 2;
    const ocy = bbox.y + bbox.h / 2;

    // Horizontal guides
    if (Math.abs(draggedBBox.x - bbox.x) < threshold) guides.push({ type: 'v', pos: bbox.x });
    if (Math.abs(draggedBBox.x + draggedBBox.w - bbox.x - bbox.w) < threshold) guides.push({ type: 'v', pos: bbox.x + bbox.w });
    if (Math.abs(cx - ocx) < threshold) guides.push({ type: 'v', pos: ocx });

    // Vertical guides
    if (Math.abs(draggedBBox.y - bbox.y) < threshold) guides.push({ type: 'h', pos: bbox.y });
    if (Math.abs(draggedBBox.y + draggedBBox.h - bbox.y - bbox.h) < threshold) guides.push({ type: 'h', pos: bbox.y + bbox.h });
    if (Math.abs(cy - ocy) < threshold) guides.push({ type: 'h', pos: ocy });
  }
  return guides;
};

export const pointInRect = (px, py, rx, ry, rw, rh) =>
  px >= rx && px <= rx + rw && py >= ry && py <= ry + rh;

export const alignObjects = (objects, alignment) => {
  if (objects.length < 2) return objects;
  const bboxes = objects.map(o => ({ obj: o, bbox: getBBox(o) }));

  switch (alignment) {
    case 'left': {
      const min = Math.min(...bboxes.map(b => b.bbox.x));
      return bboxes.map(b => ({ ...b.obj, x: min }));
    }
    case 'right': {
      const max = Math.max(...bboxes.map(b => b.bbox.x + b.bbox.w));
      return bboxes.map(b => ({ ...b.obj, x: max - b.bbox.w }));
    }
    case 'center-h': {
      const avg = bboxes.reduce((a, b) => a + b.bbox.x + b.bbox.w / 2, 0) / bboxes.length;
      return bboxes.map(b => ({ ...b.obj, x: avg - b.bbox.w / 2 }));
    }
    case 'top': {
      const min = Math.min(...bboxes.map(b => b.bbox.y));
      return bboxes.map(b => ({ ...b.obj, y: min }));
    }
    case 'bottom': {
      const max = Math.max(...bboxes.map(b => b.bbox.y + b.bbox.h));
      return bboxes.map(b => ({ ...b.obj, y: max - b.bbox.h }));
    }
    case 'center-v': {
      const avg = bboxes.reduce((a, b) => a + b.bbox.y + b.bbox.h / 2, 0) / bboxes.length;
      return bboxes.map(b => ({ ...b.obj, y: avg - b.bbox.h / 2 }));
    }
    default: return objects;
  }
};

export const distributeObjects = (objects, direction) => {
  if (objects.length < 3) return objects;
  const bboxes = objects.map(o => ({ obj: o, bbox: getBBox(o) }));
  const sorted = [...bboxes].sort((a, b) =>
    direction === 'h' ? a.bbox.x - b.bbox.x : a.bbox.y - b.bbox.y
  );

  if (direction === 'h') {
    const totalWidth = sorted.reduce((a, b) => a + b.bbox.w, 0);
    const totalSpan = sorted[sorted.length - 1].bbox.x + sorted[sorted.length - 1].bbox.w - sorted[0].bbox.x;
    const gap = (totalSpan - totalWidth) / (sorted.length - 1);
    let curX = sorted[0].bbox.x;
    return sorted.map(b => {
      const result = { ...b.obj, x: curX };
      curX += b.bbox.w + gap;
      return result;
    });
  } else {
    const totalHeight = sorted.reduce((a, b) => a + b.bbox.h, 0);
    const totalSpan = sorted[sorted.length - 1].bbox.y + sorted[sorted.length - 1].bbox.h - sorted[0].bbox.y;
    const gap = (totalSpan - totalHeight) / (sorted.length - 1);
    let curY = sorted[0].bbox.y;
    return sorted.map(b => {
      const result = { ...b.obj, y: curY };
      curY += b.bbox.h + gap;
      return result;
    });
  }
};

// ==================== PATH / BEZIER HELPERS ====================
export const pointsToPath = (points, closed = false) => {
  if (points.length === 0) return '';
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const pt = points[i];
    const hasPrevOut = prev.cx2 !== undefined && prev.cy2 !== undefined;
    const hasCurrIn = pt.cx1 !== undefined && pt.cy1 !== undefined;
    if (hasPrevOut || hasCurrIn) {
      const c1x = hasPrevOut ? prev.cx2 : prev.x;
      const c1y = hasPrevOut ? prev.cy2 : prev.y;
      const c2x = hasCurrIn ? pt.cx1 : pt.x;
      const c2y = hasCurrIn ? pt.cy1 : pt.y;
      d += ` C ${c1x} ${c1y} ${c2x} ${c2y} ${pt.x} ${pt.y}`;
    } else {
      d += ` L ${pt.x} ${pt.y}`;
    }
  }
  if (closed) d += ' Z';
  return d;
};

export const getConnectorPath = (type, x1, y1, x2, y2) => {
  if (type === 'connector-curved') {
    const mx = (x1 + x2) / 2;
    const my = (y1 + y2) / 2;
    const dx = Math.abs(x2 - x1) * 0.3;
    return `M ${x1} ${y1} C ${x1 + dx} ${y1} ${x2 - dx} ${y2} ${x2} ${y2}`;
  }
  if (type === 'connector-elbow') {
    const mx = (x1 + x2) / 2;
    return `M ${x1} ${y1} L ${mx} ${y1} L ${mx} ${y2} L ${x2} ${y2}`;
  }
  return `M ${x1} ${y1} L ${x2} ${y2}`;
};

// ==================== EXPORT ====================
export const generateCleanSVG = (objects, canvasW = 800, canvasH = 600) => {
  const defs = [];
  let body = '';

  objects.filter(o => o.visible !== false).forEach(obj => {
    const op = obj.opacity ?? 1;
    const dash = obj.dashStyle ? `stroke-dasharray="${obj.dashStyle}"` : '';
    const cap = obj.lineCap ? `stroke-linecap="${obj.lineCap}"` : '';
    const rot = obj.rotation ? `transform="rotate(${obj.rotation} ${(obj.x || 0) + (obj.w || 0) / 2} ${(obj.y || 0) + (obj.h || 0) / 2})"` : '';

    if (obj.type === 'rect') {
      body += `<rect x="${obj.x}" y="${obj.y}" width="${obj.w}" height="${obj.h}" fill="${obj.fill || 'none'}" stroke="${obj.stroke || 'none'}" stroke-width="${obj.strokeWidth || 0}" opacity="${op}" rx="${obj.rx || 0}" ${dash} ${cap} ${rot}/>\n`;
    } else if (obj.type === 'ellipse') {
      body += `<ellipse cx="${obj.x + obj.w / 2}" cy="${obj.y + obj.h / 2}" rx="${obj.w / 2}" ry="${obj.h / 2}" fill="${obj.fill || 'none'}" stroke="${obj.stroke || 'none'}" stroke-width="${obj.strokeWidth || 0}" opacity="${op}" ${dash} ${rot}/>\n`;
    } else if (obj.type === 'text') {
      const lines = (obj.text || '').split('\n');
      const fs = obj.fontSize || 16;
      const ff = obj.fontFamily || 'IBM Plex Sans';
      const fw = obj.fontWeight || 'normal';
      const fst = obj.fontStyle || 'normal';
      const anchor = obj.textAlign === 'center' ? 'middle' : obj.textAlign === 'right' ? 'end' : 'start';
      lines.forEach((line, i) => {
        body += `<text x="${obj.x}" y="${obj.y + fs * (i + 0.8) * (obj.lineHeight || 1.4)}" font-size="${fs}" fill="${obj.fill}" font-family="${ff}" font-weight="${fw}" font-style="${fst}" text-anchor="${anchor}" opacity="${op}">${escapeXml(line)}</text>\n`;
      });
    } else if (obj.type === 'line') {
      body += `<line x1="${obj.x1}" y1="${obj.y1}" x2="${obj.x2}" y2="${obj.y2}" stroke="${obj.stroke}" stroke-width="${obj.strokeWidth}" opacity="${op}" ${dash} ${cap}/>\n`;
    } else if (obj.type === 'arrow' || obj.type === 'connector-straight' || obj.type === 'connector-curved' || obj.type === 'connector-elbow') {
      const mid = `m-${obj.id}`;
      const arrowEnd = obj.arrowEnd || 'triangle';
      const arrowDef = ARROW_STYLES.find(a => a.id === arrowEnd) || ARROW_STYLES[0];
      defs.push(`<marker id="${mid}" markerWidth="12" markerHeight="10" refX="10" refY="5" orient="auto-start-reverse"><path d="${arrowDef.path}" fill="${arrowDef.fill === 'none' ? 'none' : obj.stroke}" stroke="${arrowDef.fill === 'none' ? obj.stroke : 'none'}" stroke-width="1.5"/></marker>`);
      if (obj.type === 'arrow') {
        body += `<line x1="${obj.x1}" y1="${obj.y1}" x2="${obj.x2}" y2="${obj.y2}" stroke="${obj.stroke}" stroke-width="${obj.strokeWidth}" marker-end="url(#${mid})" opacity="${op}" ${dash}/>\n`;
      } else {
        const d = getConnectorPath(obj.type, obj.x1, obj.y1, obj.x2, obj.y2);
        body += `<path d="${d}" fill="none" stroke="${obj.stroke}" stroke-width="${obj.strokeWidth}" marker-end="url(#${mid})" opacity="${op}" ${dash}/>\n`;
      }
      if (obj.label) {
        const lx = (obj.x1 + obj.x2) / 2;
        const ly = (obj.y1 + obj.y2) / 2 - 8;
        body += `<text x="${lx}" y="${ly}" font-size="11" fill="${obj.stroke}" font-family="IBM Plex Sans" text-anchor="middle">${escapeXml(obj.label)}</text>\n`;
      }
    } else if (obj.type === 'path') {
      body += `<path d="${obj.d}" fill="${obj.fill || 'none'}" stroke="${obj.stroke}" stroke-width="${obj.strokeWidth}" opacity="${op}" ${dash} ${cap}/>\n`;
    } else if (obj.type === 'polygon') {
      body += `<polygon points="${obj.points}" fill="${obj.fill}" stroke="${obj.stroke}" stroke-width="${obj.strokeWidth}" opacity="${op}" ${rot}/>\n`;
    } else if (obj.type === 'artboard') {
      body += `<rect x="${obj.x}" y="${obj.y}" width="${obj.w}" height="${obj.h}" fill="#FFFFFF" stroke="#CBD5E1" stroke-width="1" />\n`;
      body += `<text x="${obj.x}" y="${obj.y - 8}" font-size="14" fill="#0B1D3A" font-family="IBM Plex Sans" font-weight="600">${escapeXml(obj.label || '')}</text>\n`;
    } else if (obj.type === 'group') {
      // Groups are flattened for export
    }
  });

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${canvasW}" height="${canvasH}" viewBox="0 0 ${canvasW} ${canvasH}">\n<defs>\n${defs.join('\n')}\n</defs>\n<rect width="100%" height="100%" fill="white"/>\n${body}</svg>`;
};

const escapeXml = (s) => (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export const exportAsSVG = (objects, name) => {
  const svg = generateCleanSVG(objects);
  const blob = new Blob([svg], { type: 'image/svg+xml' });
  download(blob, `${name}.svg`);
};

export const exportAsPNG = async (objects, name, dpi = 300) => {
  const scale = dpi / 96;
  const w = 800 * scale;
  const h = 600 * scale;
  const svg = generateCleanSVG(objects, 800, 600);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  const img = new Image();
  return new Promise((resolve) => {
    img.onload = () => {
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob((blob) => {
        download(blob, `${name}.png`);
        resolve();
      }, 'image/png');
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));
  });
};

export const exportAsPDF = async (objects, name, preset = 'default') => {
  const { jsPDF } = await import('jspdf');
  const presets = {
    default: { w: 210, h: 297, margin: 15 },
    nature: { w: 183, h: 247, margin: 5 },
    science: { w: 174, h: 234, margin: 5 },
    cell: { w: 171, h: 229, margin: 5 },
  };
  const p = presets[preset] || presets.default;
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: [p.w, p.h] });
  const svg = generateCleanSVG(objects, 800, 600);
  const canvas = document.createElement('canvas');
  const scale = 4;
  canvas.width = 800 * scale;
  canvas.height = 600 * scale;
  const ctx = canvas.getContext('2d');
  const img = new Image();
  return new Promise((resolve) => {
    img.onload = () => {
      ctx.drawImage(img, 0, 0, 800 * scale, 600 * scale);
      const imgData = canvas.toDataURL('image/png', 1.0);
      const imgW = p.w - 2 * p.margin;
      const imgH = (imgW / 800) * 600;
      pdf.addImage(imgData, 'PNG', p.margin, p.margin, imgW, imgH);
      pdf.save(`${name}.pdf`);
      resolve();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));
  });
};

const download = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

// ==================== TEMPLATES ====================
export const BIO_TEMPLATES = {
  'Plasmid Map': [
    { type: 'ellipse', x: 250, y: 150, w: 260, h: 260, fill: 'transparent', stroke: '#0D9488', strokeWidth: 7, label: 'Backbone' },
    { type: 'rect', x: 335, y: 150, w: 80, h: 18, fill: '#0D9488', stroke: 'none', strokeWidth: 0, rotation: -35, label: 'Promoter' },
    { type: 'rect', x: 420, y: 200, w: 70, h: 18, fill: '#D97706', stroke: 'none', strokeWidth: 0, rotation: 30, label: 'GFP' },
    { type: 'rect', x: 440, y: 300, w: 70, h: 18, fill: '#7C3AED', stroke: 'none', strokeWidth: 0, rotation: 80, label: 'AmpR' },
    { type: 'rect', x: 310, y: 350, w: 60, h: 18, fill: '#3B82F6', stroke: 'none', strokeWidth: 0, rotation: 140, label: 'ORI' },
    { type: 'text', x: 345, y: 268, text: 'pXYZ\n5.2 kb', fontSize: 18, fill: '#0B1D3A', textAlign: 'center' },
    { type: 'text', x: 600, y: 160, text: 'Promoter', fontSize: 12, fill: '#0D9488' },
    { type: 'text', x: 600, y: 210, text: 'GFP', fontSize: 12, fill: '#D97706' },
    { type: 'text', x: 600, y: 260, text: 'AmpR', fontSize: 12, fill: '#7C3AED' },
    { type: 'text', x: 600, y: 310, text: 'ORI', fontSize: 12, fill: '#3B82F6' },
    { type: 'line', x1: 440, y1: 165, x2: 595, y2: 155, stroke: '#94A3B8', strokeWidth: 1, dashStyle: '4 2' },
    { type: 'line', x1: 470, y1: 215, x2: 595, y2: 205, stroke: '#94A3B8', strokeWidth: 1, dashStyle: '4 2' },
    { type: 'line', x1: 470, y1: 310, x2: 595, y2: 255, stroke: '#94A3B8', strokeWidth: 1, dashStyle: '4 2' },
    { type: 'line', x1: 350, y1: 365, x2: 595, y2: 305, stroke: '#94A3B8', strokeWidth: 1, dashStyle: '4 2' },
  ],
  'Gel Electrophoresis': [
    { type: 'rect', x: 80, y: 60, w: 540, h: 420, fill: '#0F172A', stroke: '#334155', strokeWidth: 2, label: 'Gel' },
    { type: 'rect', x: 100, y: 80, w: 500, h: 5, fill: '#475569', stroke: 'none', strokeWidth: 0 },
    { type: 'text', x: 105, y: 505, text: 'Ladder', fontSize: 10, fill: '#94A3B8' },
    { type: 'rect', x: 120, y: 100, w: 12, h: 340, fill: '#4ADE80', stroke: 'none', strokeWidth: 0 },
    { type: 'rect', x: 200, y: 140, w: 12, h: 200, fill: '#38BDF8', stroke: 'none', strokeWidth: 0 },
    { type: 'rect', x: 280, y: 120, w: 12, h: 260, fill: '#38BDF8', stroke: 'none', strokeWidth: 0 },
    { type: 'rect', x: 360, y: 180, w: 12, h: 140, fill: '#38BDF8', stroke: 'none', strokeWidth: 0 },
    { type: 'rect', x: 440, y: 100, w: 12, h: 300, fill: '#FB923C', stroke: 'none', strokeWidth: 0 },
    { type: 'rect', x: 520, y: 160, w: 12, h: 180, fill: '#FB923C', stroke: 'none', strokeWidth: 0 },
    { type: 'text', x: 195, y: 505, text: 'S1', fontSize: 10, fill: '#94A3B8' },
    { type: 'text', x: 275, y: 505, text: 'S2', fontSize: 10, fill: '#94A3B8' },
    { type: 'text', x: 355, y: 505, text: 'S3', fontSize: 10, fill: '#94A3B8' },
    { type: 'text', x: 435, y: 505, text: 'C1', fontSize: 10, fill: '#94A3B8' },
    { type: 'text', x: 515, y: 505, text: 'C2', fontSize: 10, fill: '#94A3B8' },
    { type: 'text', x: 650, y: 100, text: '10 kb', fontSize: 9, fill: '#64748B' },
    { type: 'text', x: 650, y: 200, text: '3 kb', fontSize: 9, fill: '#64748B' },
    { type: 'text', x: 650, y: 300, text: '1 kb', fontSize: 9, fill: '#64748B' },
    { type: 'text', x: 650, y: 400, text: '0.5 kb', fontSize: 9, fill: '#64748B' },
  ],
  'Signaling Pathway': [
    { type: 'text', x: 250, y: 40, text: 'Signal Transduction Pathway', fontSize: 18, fill: '#0B1D3A', fontWeight: 'bold' },
    { type: 'rect', x: 30, y: 100, w: 130, h: 55, fill: '#DBEAFE', stroke: '#3B82F6', strokeWidth: 2, rx: 6 },
    { type: 'text', x: 55, y: 130, text: 'Ligand', fontSize: 13, fill: '#1E40AF' },
    { type: 'arrow', x1: 160, y1: 127, x2: 220, y2: 127, stroke: '#64748B', strokeWidth: 2, arrowEnd: 'triangle' },
    { type: 'rect', x: 220, y: 100, w: 130, h: 55, fill: '#FEF3C7', stroke: '#D97706', strokeWidth: 2, rx: 6 },
    { type: 'text', x: 240, y: 130, text: 'Receptor', fontSize: 13, fill: '#92400E' },
    { type: 'arrow', x1: 350, y1: 127, x2: 410, y2: 127, stroke: '#64748B', strokeWidth: 2, arrowEnd: 'triangle' },
    { type: 'rect', x: 410, y: 100, w: 130, h: 55, fill: '#FCE7F3', stroke: '#DB2777', strokeWidth: 2, rx: 6 },
    { type: 'text', x: 430, y: 130, text: 'Kinase A', fontSize: 13, fill: '#9D174D' },
    { type: 'arrow', x1: 475, y1: 155, x2: 475, y2: 220, stroke: '#64748B', strokeWidth: 2, arrowEnd: 'triangle' },
    { type: 'rect', x: 410, y: 220, w: 130, h: 55, fill: '#EDE9FE', stroke: '#7C3AED', strokeWidth: 2, rx: 6 },
    { type: 'text', x: 430, y: 250, text: 'Kinase B', fontSize: 13, fill: '#5B21B6' },
    { type: 'arrow', x1: 475, y1: 275, x2: 475, y2: 340, stroke: '#64748B', strokeWidth: 2, arrowEnd: 'triangle' },
    { type: 'rect', x: 390, y: 340, w: 170, h: 65, fill: '#D1FAE5', stroke: '#059669', strokeWidth: 2, rx: 6 },
    { type: 'text', x: 410, y: 365, text: 'Transcription', fontSize: 13, fill: '#065F46' },
    { type: 'text', x: 430, y: 385, text: 'Factor', fontSize: 13, fill: '#065F46' },
    { type: 'arrow', x1: 560, y1: 372, x2: 630, y2: 372, stroke: '#64748B', strokeWidth: 2, arrowEnd: 'triangle' },
    { type: 'rect', x: 630, y: 340, w: 130, h: 65, fill: '#FEF9C3', stroke: '#CA8A04', strokeWidth: 2, rx: 6 },
    { type: 'text', x: 650, y: 370, text: 'Gene\nExpression', fontSize: 12, fill: '#854D0E' },
  ],
  'Cell Diagram': [
    { type: 'text', x: 280, y: 30, text: 'Eukaryotic Cell', fontSize: 20, fill: '#0B1D3A', fontWeight: 'bold' },
    { type: 'ellipse', x: 100, y: 60, w: 560, h: 440, fill: '#FEF9C3', stroke: '#CA8A04', strokeWidth: 3 },
    { type: 'ellipse', x: 280, y: 180, w: 180, h: 160, fill: '#DBEAFE', stroke: '#3B82F6', strokeWidth: 2 },
    { type: 'ellipse', x: 315, y: 220, w: 80, h: 60, fill: '#1E40AF', stroke: '#1E3A8A', strokeWidth: 1 },
    { type: 'text', x: 330, y: 260, text: 'Nucleolus', fontSize: 9, fill: '#FFFFFF' },
    { type: 'text', x: 325, y: 365, text: 'Nucleus', fontSize: 12, fill: '#1E40AF' },
    { type: 'ellipse', x: 160, y: 140, w: 80, h: 50, fill: '#BBF7D0', stroke: '#22C55E', strokeWidth: 1.5 },
    { type: 'ellipse', x: 170, y: 230, w: 70, h: 45, fill: '#BBF7D0', stroke: '#22C55E', strokeWidth: 1.5 },
    { type: 'text', x: 160, y: 210, text: 'ER', fontSize: 10, fill: '#15803D' },
    { type: 'ellipse', x: 500, y: 150, w: 55, h: 40, fill: '#FECACA', stroke: '#EF4444', strokeWidth: 1.5 },
    { type: 'ellipse', x: 520, y: 250, w: 50, h: 35, fill: '#FECACA', stroke: '#EF4444', strokeWidth: 1.5 },
    { type: 'ellipse', x: 480, y: 330, w: 55, h: 40, fill: '#FECACA', stroke: '#EF4444', strokeWidth: 1.5 },
    { type: 'text', x: 505, y: 210, text: 'Mitochondria', fontSize: 10, fill: '#DC2626' },
    { type: 'ellipse', x: 180, y: 350, w: 100, h: 60, fill: '#E9D5FF', stroke: '#8B5CF6', strokeWidth: 1.5 },
    { type: 'text', x: 195, y: 390, text: 'Golgi', fontSize: 11, fill: '#7C3AED' },
    { type: 'ellipse', x: 430, y: 400, w: 60, h: 50, fill: '#CFFAFE', stroke: '#06B6D4', strokeWidth: 1.5 },
    { type: 'text', x: 435, y: 435, text: 'Lysosome', fontSize: 9, fill: '#0891B2' },
  ],
  'CRISPR-Cas9 Edit': [
    { type: 'text', x: 220, y: 50, text: 'CRISPR-Cas9 Gene Editing', fontSize: 20, fill: '#0B1D3A', fontWeight: 'bold' },
    { type: 'rect', x: 40, y: 230, w: 720, h: 14, fill: '#3B82F6', stroke: 'none', strokeWidth: 0 },
    { type: 'rect', x: 40, y: 250, w: 720, h: 14, fill: '#60A5FA', stroke: 'none', strokeWidth: 0 },
    { type: 'text', x: 40, y: 290, text: "5' ─────────────────────────────── 3'", fontSize: 10, fill: '#3B82F6', fontFamily: 'IBM Plex Mono' },
    { type: 'text', x: 40, y: 225, text: "3' ─────────────────────────────── 5'", fontSize: 10, fill: '#60A5FA', fontFamily: 'IBM Plex Mono' },
    { type: 'rect', x: 300, y: 222, w: 120, h: 8, fill: '#EF4444', stroke: 'none', strokeWidth: 0 },
    { type: 'text', x: 330, y: 320, text: 'PAM (NGG)', fontSize: 11, fill: '#DC2626', fontWeight: 'bold' },
    { type: 'rect', x: 280, y: 120, w: 160, h: 70, fill: '#FEF3C7', stroke: '#D97706', strokeWidth: 2, rx: 8 },
    { type: 'text', x: 325, y: 165, text: 'Cas9', fontSize: 18, fill: '#92400E', fontWeight: 'bold' },
    { type: 'rect', x: 240, y: 90, w: 240, h: 24, fill: '#BBF7D0', stroke: '#22C55E', strokeWidth: 1.5, rx: 4 },
    { type: 'text', x: 300, y: 108, text: 'sgRNA Guide Sequence', fontSize: 11, fill: '#15803D' },
    { type: 'text', x: 300, y: 380, text: 'Double-Strand Break (DSB)', fontSize: 13, fill: '#DC2626', fontWeight: 'bold' },
    { type: 'line', x1: 360, y1: 264, x2: 360, y2: 350, stroke: '#EF4444', strokeWidth: 2, dashStyle: '4 3' },
    { type: 'arrow', x1: 100, y1: 430, x2: 280, y2: 430, stroke: '#059669', strokeWidth: 2, arrowEnd: 'triangle' },
    { type: 'text', x: 120, y: 455, text: 'NHEJ (Error-prone)', fontSize: 11, fill: '#059669' },
    { type: 'arrow', x1: 450, y1: 430, x2: 680, y2: 430, stroke: '#7C3AED', strokeWidth: 2, arrowEnd: 'triangle' },
    { type: 'text', x: 470, y: 455, text: 'HDR (Precise editing)', fontSize: 11, fill: '#7C3AED' },
  ],
  'Multi-Panel (A-D)': [
    { type: 'artboard', x: 20, y: 20, w: 370, h: 260, label: 'A' },
    { type: 'artboard', x: 410, y: 20, w: 370, h: 260, label: 'B' },
    { type: 'artboard', x: 20, y: 310, w: 370, h: 260, label: 'C' },
    { type: 'artboard', x: 410, y: 310, w: 370, h: 260, label: 'D' },
  ],
  'Flow Diagram': [
    { type: 'rect', x: 300, y: 30, w: 180, h: 50, fill: '#0D9488', stroke: 'none', strokeWidth: 0, rx: 6 },
    { type: 'text', x: 350, y: 62, text: 'Start', fontSize: 14, fill: '#FFFFFF', textAlign: 'center' },
    { type: 'arrow', x1: 390, y1: 80, x2: 390, y2: 130, stroke: '#64748B', strokeWidth: 2, arrowEnd: 'triangle' },
    { type: 'rect', x: 300, y: 130, w: 180, h: 50, fill: '#DBEAFE', stroke: '#3B82F6', strokeWidth: 2, rx: 6 },
    { type: 'text', x: 330, y: 162, text: 'Process 1', fontSize: 13, fill: '#1E40AF' },
    { type: 'arrow', x1: 390, y1: 180, x2: 390, y2: 230, stroke: '#64748B', strokeWidth: 2, arrowEnd: 'triangle' },
    { type: 'rect', x: 300, y: 230, w: 180, h: 50, fill: '#FEF3C7', stroke: '#D97706', strokeWidth: 2, rx: 6 },
    { type: 'text', x: 325, y: 262, text: 'Decision', fontSize: 13, fill: '#92400E' },
    { type: 'arrow', x1: 390, y1: 280, x2: 390, y2: 330, stroke: '#64748B', strokeWidth: 2, arrowEnd: 'triangle' },
    { type: 'rect', x: 300, y: 330, w: 180, h: 50, fill: '#FCE7F3', stroke: '#DB2777', strokeWidth: 2, rx: 6 },
    { type: 'text', x: 330, y: 362, text: 'Process 2', fontSize: 13, fill: '#9D174D' },
    { type: 'arrow', x1: 390, y1: 380, x2: 390, y2: 430, stroke: '#64748B', strokeWidth: 2, arrowEnd: 'triangle' },
    { type: 'rect', x: 300, y: 430, w: 180, h: 50, fill: '#D1FAE5', stroke: '#059669', strokeWidth: 2, rx: 6 },
    { type: 'text', x: 355, y: 462, text: 'End', fontSize: 14, fill: '#065F46', textAlign: 'center' },
    { type: 'arrow', x1: 480, y1: 255, x2: 580, y2: 255, stroke: '#D97706', strokeWidth: 1.5, arrowEnd: 'triangle', dashStyle: '4 3' },
    { type: 'text', x: 590, y: 262, text: 'No', fontSize: 11, fill: '#D97706' },
    { type: 'text', x: 395, y: 315, text: 'Yes', fontSize: 11, fill: '#64748B' },
  ],
  'Experimental Pipeline': [
    { type: 'text', x: 200, y: 35, text: 'Experimental Pipeline', fontSize: 20, fill: '#0B1D3A', fontWeight: 'bold' },
    { type: 'rect', x: 30, y: 80, w: 140, h: 70, fill: '#DBEAFE', stroke: '#3B82F6', strokeWidth: 2, rx: 8 },
    { type: 'text', x: 50, y: 115, text: 'Sample\nPreparation', fontSize: 12, fill: '#1E40AF' },
    { type: 'arrow', x1: 170, y1: 115, x2: 220, y2: 115, stroke: '#64748B', strokeWidth: 2, arrowEnd: 'triangle' },
    { type: 'rect', x: 220, y: 80, w: 140, h: 70, fill: '#FEF3C7', stroke: '#D97706', strokeWidth: 2, rx: 8 },
    { type: 'text', x: 245, y: 115, text: 'DNA\nExtraction', fontSize: 12, fill: '#92400E' },
    { type: 'arrow', x1: 360, y1: 115, x2: 410, y2: 115, stroke: '#64748B', strokeWidth: 2, arrowEnd: 'triangle' },
    { type: 'rect', x: 410, y: 80, w: 140, h: 70, fill: '#FCE7F3', stroke: '#DB2777', strokeWidth: 2, rx: 8 },
    { type: 'text', x: 450, y: 115, text: 'PCR', fontSize: 14, fill: '#9D174D', fontWeight: 'bold' },
    { type: 'arrow', x1: 550, y1: 115, x2: 600, y2: 115, stroke: '#64748B', strokeWidth: 2, arrowEnd: 'triangle' },
    { type: 'rect', x: 600, y: 80, w: 140, h: 70, fill: '#D1FAE5', stroke: '#059669', strokeWidth: 2, rx: 8 },
    { type: 'text', x: 615, y: 115, text: 'Gel\nAnalysis', fontSize: 12, fill: '#065F46' },
    { type: 'rect', x: 30, y: 200, w: 140, h: 70, fill: '#EDE9FE', stroke: '#7C3AED', strokeWidth: 2, rx: 8 },
    { type: 'text', x: 48, y: 235, text: 'Cloning &\nTransformation', fontSize: 11, fill: '#5B21B6' },
    { type: 'arrow', x1: 170, y1: 235, x2: 220, y2: 235, stroke: '#64748B', strokeWidth: 2, arrowEnd: 'triangle' },
    { type: 'rect', x: 220, y: 200, w: 140, h: 70, fill: '#FEF9C3', stroke: '#CA8A04', strokeWidth: 2, rx: 8 },
    { type: 'text', x: 240, y: 235, text: 'Selection &\nScreening', fontSize: 12, fill: '#854D0E' },
    { type: 'arrow', x1: 360, y1: 235, x2: 410, y2: 235, stroke: '#64748B', strokeWidth: 2, arrowEnd: 'triangle' },
    { type: 'rect', x: 410, y: 200, w: 140, h: 70, fill: '#CFFAFE', stroke: '#06B6D4', strokeWidth: 2, rx: 8 },
    { type: 'text', x: 430, y: 235, text: 'Sequencing\nVerification', fontSize: 11, fill: '#0891B2' },
    { type: 'arrow', x1: 550, y1: 235, x2: 600, y2: 235, stroke: '#64748B', strokeWidth: 2, arrowEnd: 'triangle' },
    { type: 'rect', x: 600, y: 200, w: 140, h: 70, fill: '#0D9488', stroke: 'none', strokeWidth: 0, rx: 8 },
    { type: 'text', x: 625, y: 235, text: 'Data\nAnalysis', fontSize: 12, fill: '#FFFFFF' },
  ],
};
